import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, mediaUrl } from "@/lib/api";

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) {
      setSettings({});
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (!settings) return;
    const body = document.body;
    body.setAttribute("data-theme", settings.theme_mode || "light_parchment");
    if (settings.background_image) {
      body.classList.add("app-bg-image");
      body.style.setProperty("--bg-image", `url("${mediaUrl(settings.background_image)}")`);
    } else {
      body.classList.remove("app-bg-image");
      body.style.removeProperty("--bg-image");
    }
    // Dynamic fonts applied across all text
    const disp = settings.font_display || "Cormorant Garamond";
    const bodyFont = settings.font_body || "Manrope";
    const fam = (n) => n.replace(/ /g, "+");
    const href = `https://fonts.googleapis.com/css2?family=${fam(disp)}:wght@500;600;700&family=${fam(bodyFont)}:wght@400;500;600;700;800&display=swap`;
    let link = document.getElementById("dynamic-fonts");
    if (!link) {
      link = document.createElement("link");
      link.id = "dynamic-fonts";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = href;
    const root = document.documentElement;
    root.style.setProperty("--font-display", `'${disp}', Georgia, serif`);
    root.style.setProperty("--font-body", `'${bodyFont}', system-ui, sans-serif`);
  }, [settings]);

  const value = useMemo(
    () => ({ settings, setSettings, loadSettings }),
    [settings, loadSettings]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export const useSettings = () => useContext(SettingsContext);
